import React, { useState } from 'react';
import env from '../../../Env';
import { useMutate } from 'restful-react';
import { TextInput } from '../../FormComponents/TextInput/TextInput';
import { BaseModal } from '../BaseModal/BaseModal';
import './InviteProjectUserModal.scss';
import { Button } from '../../FormComponents/Buttons/Button';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import useProject from '../../../hooks/useProject';
import ErrorMessage from '../../ErrorMessage/ErrorMessage';
import { useTranslation } from 'react-i18next';
import { ProjectUser } from '../../../types/ProjectUser';
import { ValidationError } from '../../FormComponents/ValidationError/ValidationError';
import _ from 'lodash';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';
import { UserType, UserTypeEnum } from '../../../types/enums/UserTypesEnum';
import { CaptionModeEnum } from '../../../types/enums/CaptionModeEnum';
import { Project } from '../../../types/Project';

type Props = {
  isOpen: boolean;
  contentLabel: string;
  selectedProjectUser: ProjectUser;
  userType: UserType;
  handleCloseModal: () => void;
};

type CreateInviteProjectUserFormData = {
  name: string;
  emailAddress: string;
  userType: UserType;
};

type DeleteInvitedProjectUserFormData = {
  id: string;
  userType: UserType;
};

type InviteProjectUserData = {
  emailAddress: string;
  id: string;
  name: string;
  userType: UserType;
};

type SuccessfulInviteProjectUserData = {
  id: string;
  accessToken: string;
};

export const InviteProjectUserModal = (props: Props) => {
  const { t } = useTranslation();
  const { project, setProject, updateStream } = useProject();
  const [copied, setCopied] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [validationKey, setValidationKey] = useState(
    'producer.control-panel.invites.invite-email-missing-value',
  );
  const [loadingCreate, setLoadingCreate] = useState(false);

  const defaultValues: CreateInviteProjectUserFormData = {
    name: props.selectedProjectUser.name,
    emailAddress: props.selectedProjectUser.emailAddress,
    userType: props.userType,
  };

  const { control, errors, register, handleSubmit } = useForm<CreateInviteProjectUserFormData>({
    defaultValues,
  });

  const copyToClipboard = (copyValue: string) => {
    if (props.userType === UserTypeEnum.Captioner) {
      copyValue = `${env.authRedirect}/captioner/${copyValue}`;
    }

    navigator.clipboard.writeText(copyValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 5000);
  };

  const removeProjectUserFromProject = (projectUserId: string) => {
    const newProject = _.cloneDeep(project) as Project;
    const itemIndex = newProject?.projectUsers?.map((item) => item.id).indexOf(projectUserId);

    if (itemIndex != null && itemIndex !== -1) {
      newProject?.projectUsers?.splice(itemIndex, 1);
    }

    if (project !== null && newProject !== null) {
      setProject(newProject);
    }
  };

  const addProjectUserToProject = (projectUser: ProjectUser) => {
    const newProject = _.cloneDeep(project);

    newProject?.projectUsers?.push(projectUser);

    if (newProject?.encoder?.captionMode === CaptionModeEnum.None) {
      newProject.encoder.captionMode = CaptionModeEnum.Open;
    }

    if (project !== null && newProject !== null) {
      setTimeout(() => {
        setProject(newProject);
      }, 10);
    }
  };

  const onSuccessfulCreate = (body: InviteProjectUserData, data: unknown) => {
    const typedData = data as {
      response: string;
      statusCode: number;
      result: SuccessfulInviteProjectUserData;
    };

    if (typedData.statusCode === 200) {
      const newProjectUser: ProjectUser = {
        id: typedData.result.id,
        projectId: typedData.result.id,
        name: body.name,
        emailAddress: body.emailAddress,
        userType: props.userType,
        accessToken: typedData.result.accessToken,
      };

      if (
        props.userType === UserTypeEnum.Captioner &&
        project?.encoder?.captionMode === CaptionModeEnum.None
      ) {
        updateStream({ captionMode: CaptionModeEnum.Open });
      }

      addProjectUserToProject(newProjectUser);

      setTimeout(function () {
        props.handleCloseModal();
      }, 1000);
    }
  };

  const onSuccessfulDelete = (body: DeleteInvitedProjectUserFormData, data: unknown) => {
    const typedData = data as { response: string; statusCode: number };
    if (typedData.statusCode === 200) {
      removeProjectUserFromProject(body.id);
      props.handleCloseModal();
    }
  };

  const { mutate: createInviteProjectUser } = useMutate<CreateInviteProjectUserFormData>({
    verb: 'POST',
    path: '/projects/' + project?.id + '/users',
    onMutate: onSuccessfulCreate,
  });

  const {
    mutate: deleteInvitedProjectUser,
    loading: loadingDelete,
    error: errorDelete,
  } = useMutate<DeleteInvitedProjectUserFormData>({
    verb: 'DELETE',
    path: '/projects/' + project?.id + '/users',
    onMutate: onSuccessfulDelete,
  });

  const onSubmit: SubmitHandler<CreateInviteProjectUserFormData> = (
    data: CreateInviteProjectUserFormData,
    event?: React.BaseSyntheticEvent,
  ) => {
    setLoadingCreate(true);
    event?.preventDefault();
    createInviteProjectUser({ ...data, id: props.selectedProjectUser.id }).catch((error) => {
      if (error.data.errors[0].indexOf('EmailAddressInuseException') > -1) {
        setValidationKey('producer.control-panel.invites.invite-email-in-use');
      } else {
        setValidationKey('producer.control-panel.invites.invite-email-not-in-streamtext');
      }

      setHasError(true);
      setLoadingCreate(false);

      const value: string = control.getValues().emailAddress;
      control.setValue('emailAddress', '');
      control.setValue('emailAddress', value, { shouldValidate: true });
    });
  };

  const onDelete = (data: DeleteInvitedProjectUserFormData) => {
    deleteInvitedProjectUser(data);
  };

  const isValidEmailAddress = (email: string) => {
    // eslint-disable-next-line
    const re = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email);
  };

  const validateEmailAddress = (email: string) => (hasError ? false : isValidEmailAddress(email));

  if (errorDelete) {
    return <ErrorMessage message={t('producer.control-panel.invites.invite-delete')} />;
  }

  const getEmailValidationKey = (value: string) => {
    if (hasError) {
      return validationKey;
    }

    if (value.length <= 320) {
      return 'producer.control-panel.invites.invite-email-missing-value';
    }

    return 'producer.control-panel.invites.invite-email-max-length';
  };

  const getTitle = () => {
    if (props.userType === UserTypeEnum.Captioner) {
      return 'producer.control-panel.invites.invite-captioner-modal-title';
    }

    if (props.userType === UserTypeEnum.Connector) {
      return 'producer.control-panel.invites.invite-connector-modal-title';
    }

    return '';
  };

  return (
    <BaseModal
      isOpen={props.isOpen}
      contentLabel={props.contentLabel}
      handleCloseModal={props.handleCloseModal}
      ariaHideApp={true}
      title={getTitle()}
    >
      <form className="invite-project-user-modal" onSubmit={handleSubmit(onSubmit)}>
        <input
          name="userType"
          defaultValue={defaultValues.userType}
          type="hidden"
          ref={() => register({ name: 'userType' })}
        />
        <Controller
          name="name"
          control={control}
          rules={{ required: true, maxLength: 150 }}
          render={({ onChange, value }) => (
            <>
              <label>{t('producer.control-panel.invites.invite-name-title')}</label>
              <TextInput
                placeholder="producer.control-panel.invites.invite-name-placeholder"
                value={value}
                onChange={onChange}
                onAfterChange={null}
                errorType={errors.name}
                errorMessageKey={
                  value.length <= 150
                    ? 'producer.control-panel.invites.invite-name-missing-value'
                    : 'producer.control-panel.invites.invite-name-max-length'
                }
                readOnly={props.selectedProjectUser.id !== ''}
              />
            </>
          )}
        />
        <ValidationError
          errorType={errors.emailAddress}
          errorMessageKey={errors.emailAddress?.message ?? ''}
        />
        <Controller
          name="emailAddress"
          control={control}
          rules={{
            required: true,
            maxLength: 320,
            validate: (value) => validateEmailAddress(value),
          }}
          render={({ onChange, value }) => (
            <>
              <label>{t('producer.control-panel.invites.invite-email-title')}</label>
              <TextInput
                placeholder="producer.control-panel.invites.invite-email-placeholder"
                value={value}
                onChange={(e) => {
                  setHasError(false);
                  onChange(e);
                }}
                onAfterChange={null}
                errorType={errors.emailAddress}
                errorMessageKey={getEmailValidationKey(value)}
                readOnly={props.selectedProjectUser.id !== ''}
              />
            </>
          )}
        />
        <br />
        {props.selectedProjectUser.id === '' ? (
          <>
            {!loadingCreate ? (
              <Button
                translationKey="producer.control-panel.invites.send"
                variant="primary"
                color="blue"
                onClick={() => null}
                type="submit"
                disabled={loadingCreate}
              />
            ) : (
              <LoadingSpinner />
            )}
          </>
        ) : null}
      </form>
      {props.selectedProjectUser.id !== '' ? (
        <>
          {props.selectedProjectUser?.accessToken ? (
            <Button
              translationKey={
                copied
                  ? 'producer.control-panel.invites.copied'
                  : `producer.control-panel.invites.copy${(props.userType === UserTypeEnum.Connector ? '-connector' : '')}`
              }
              variant="secondary"
              color={copied ? 'green' : 'blue'}
              onClick={() =>
                copyToClipboard(
                    props.selectedProjectUser.accessToken,
                )
              }
              type="button"
              disabled={loadingDelete}
            />
          ) : null}
          {!loadingDelete ? (
            <Button
              translationKey="producer.control-panel.invites.delete"
              variant="secondary"
              color="blue"
              onClick={() =>
                onDelete({
                  id: props.selectedProjectUser.id,
                  userType: props.userType,
                })
              }
              type={undefined}
              disabled={loadingDelete}
            />
          ) : (
            <LoadingSpinner />
          )}
        </>
      ) : null}
    </BaseModal>
  );
};
